// client/src/components/AlarmsView.jsx
// Plant-scoped alarms tab. Three panes:
//   1. Stats strip (active / unack / shelved / today)
//   2. Active events list (with Ack / Shelve buttons)
//   3. Alarm configs list (Add / Edit / Delete) — the editor opens a modal
//      with the FilterBuilder (AND/OR groups, IN / NOT IN / BETWEEN / LIKE ...)
//
// Uses the same dark palette as Metrics / Admin views.

import { useEffect, useMemo, useState, useRef } from 'react';
import { Activity, AlertTriangle, Bell, Plus, RefreshCw, Trash2, Edit2, X, Check, PauseCircle, PlayCircle, Factory, Search, Clock, History, ChevronUp, ChevronDown, Users, ArrowUpRight } from 'lucide-react';
import { API_URL } from '../config.js';
import alarmService from '../services/alarmService.js';
import eventStream from '../services/eventStream.js';
import mysqlService from '../services/mysql.js';
import questdbService from '../services/questdb.js';
import FilterBuilder from './AlarmsView/FilterBuilder.jsx';

const C = {
  pageBg:     '#0a0e1a',
  cardBg:     'rgba(255,255,255,0.025)',     // subtle wash on top of pageBg, no hard border needed
  cardBorder: 'rgba(255,255,255,0.06)',      // hairline
  border:     '#1e293b',                      // keep for legacy table cells, modal divider
  input:      '#0b1220',
  text:       '#e5e7eb',
  muted:      '#94a3b8',
  accent:     '#a78bfa',
  accentSoft: 'rgba(167,139,250,0.12)',
  good:       '#22c55e',
  warn:       '#f59e0b',
  bad:        '#ef4444'
};
const ipt = {
  padding: '8px 12px',
  background: C.input,
  border: `1px solid ${C.cardBorder}`,
  borderRadius: 6,
  color: C.text,
  fontSize: 13,
  outline: 'none',
  transition: 'border-color 0.15s, box-shadow 0.15s',
  boxSizing: 'border-box'
  // NOTE: no width — header buttons/selects sit inline. Modal Row gives 1fr column → inputs stretch there.
};
const btn = { ...ipt, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 };
// Borderless icon-button for table row actions — much lighter visually
const ghostBtn = {
  padding: '6px 8px', background: 'transparent', border: 'none',
  borderRadius: 4, color: C.muted, cursor: 'pointer', outline: 'none',
  display: 'inline-flex', alignItems: 'center', gap: 4, transition: 'all 0.15s'
};

const PRIORITY_STYLE = {
  critical: { color: '#fca5a5', bg: 'rgba(239,68,68,0.12)' },
  high:     { color: '#fdba74', bg: 'rgba(245,158,11,0.12)' },
  medium:   { color: '#facc15', bg: 'rgba(234,179,8,0.12)' },
  low:      { color: '#86efac', bg: 'rgba(34,197,94,0.12)' }
};

const EMPTY_CONFIG = {
  name: '', description: '', dbEngine: 'mysql', mysqlDatabase: '', table: '',
  tagField: '', timestampField: 'timestamp',
  alarmType: 'HI', limitValue: 0, unit: '', deadband: 0, delaySeconds: 0,
  priority: 'medium', notifGroupId: '', enabled: true,
  filters: { combinator: 'AND', rules: [] },
  escalations: []
};

// Small global stylesheet — handles things inline styles can't do:
// :focus rings for inputs, :hover for ghost buttons, scrollbar styling.
function GlobalStyles() {
  return (
    <style>{`
      .miralys-modal input,
      .miralys-modal select,
      .miralys-modal textarea {
        width: 100%; box-sizing: border-box;
      }
      .miralys-modal input:focus,
      .miralys-modal select:focus,
      .miralys-modal textarea:focus {
        border-color: ${C.accent} !important;
        box-shadow: 0 0 0 3px rgba(167,139,250,0.15) !important;
      }
      .miralys-modal select { appearance: none; -webkit-appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 8'%3E%3Cpath fill='%2394a3b8' d='M6 8L0 0h12z'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 12px center; background-size: 8px; padding-right: 32px; }
      .miralys-ghost:hover { background: rgba(255,255,255,0.04) !important; color: #cbd5e1 !important; }
    `}</style>
  );
}

// Wrapper that adds a plant picker so admins can switch between plants without
// leaving the tab. The inner view does all the actual work.
export default function AlarmsView({ plantId: forcedPlantId }) {
  const [plants, setPlants]       = useState([]);
  const [plantId, setPlantId]     = useState(forcedPlantId || '');

  useEffect(() => {
    if (forcedPlantId) return; // when used embedded in a plant context, skip picker
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const user  = JSON.parse(localStorage.getItem('user') || '{}');
        const isAdmin = user?.globalRole === 'admin' || user?.globalRole === 'superadmin';

        let list = [];
        if (isAdmin) {
          // Admins can see all plants
          const r = await fetch(`${API_URL}/admin/plants`, { headers: { Authorization: `Bearer ${token}` } });
          if (r.ok) {
            const data = await r.json();
            list = (data.plants || []).map(p => ({ plantId: p.plantId || p.id, name: p.name || p.plantId }));
          }
        } else {
          // Non-admin: derive from user.plantAccess (already stored on login)
          list = (user.plantAccess || []).map(pa => ({ plantId: pa.plantId, name: pa.plantId }));
        }
        setPlants(list);
        if (list.length && !plantId) setPlantId(list[0].plantId);
      } catch (e) {
        console.warn('[AlarmsView] plant list fetch failed:', e.message);
      }
    })();
  }, [forcedPlantId]);

  return (
    <>
      <GlobalStyles />
      <AlarmsInner
        plantId={plantId}
        plants={forcedPlantId ? [] : plants}
        onChangePlant={forcedPlantId ? null : setPlantId}
      />
    </>
  );
}

function AlarmsInner({ plantId, plants = [], onChangePlant = null }) {
  const [stats, setStats]     = useState(null);
  const [configs, setConfigs] = useState([]);
  const [events, setEvents]   = useState([]);
  const [groups, setGroups]   = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [editing, setEditing] = useState(null);   // config object being edited (or null)
  const [tab, setTab]         = useState('overview');   // overview | active | rules | groups | history
  const [recentPulse, setRecentPulse] = useState(new Set());   // eventIds that just arrived (for fade-in)

  async function loadAll() {
    if (!plantId) return;
    setLoading(true);
    // Capture the plantId at request time. If the user types another
    // character before this fetch returns, plantId will have changed and
    // we discard this (stale) response.
    const requestedPlant = plantId;
    try {
      const [s, c, e, g] = await Promise.all([
        alarmService.stats(plantId).catch(() => null),
        alarmService.listConfigs(plantId).catch(() => []),
        alarmService.listActive(plantId).catch(() => []),
        alarmService.listGroups(plantId).catch(() => [])
      ]);
      if (requestedPlant !== plantId) return;
      setStats(s); setConfigs(c); setEvents(e); setGroups(g); setError(null);
    } catch (err) {
      if (requestedPlant === plantId) setError(err.message);
    }
    finally {
      if (requestedPlant === plantId) setLoading(false);
    }
  }

  async function loadHistory() {
    if (!plantId) return;
    try {
      const h = await alarmService.listHistory(plantId, { limit: 200 });
      setHistory(h || []);
    } catch (err) {
      console.warn('[AlarmsView] history fetch failed:', err.message);
    }
  }

  // Lazy-load history when the tab is first opened
  useEffect(() => {
    if (tab === 'history') loadHistory();
  }, [tab, plantId]);

  // Mark a newly-arrived event for fade-in animation
  function flashNew(eventId) {
    if (!eventId) return;
    setRecentPulse(prev => new Set(prev).add(eventId));
    setTimeout(() => {
      setRecentPulse(prev => { const n = new Set(prev); n.delete(eventId); return n; });
    }, 3000);
  }

  useEffect(() => {
    loadAll();

    // Real-time updates via SSE — replaces 10-second polling. We refetch
    // the affected slices on each event rather than mutating state in place,
    // which keeps the rendering predictable and matches the server's view.
    // The event bus carries everything tenant-filtered, so we don't get
    // events for other plants.
    const matchesPlant = (p) => !p?.tenantId || p.tenantId === plantId;
    const onTrigger = (p) => {
      if (!matchesPlant(p)) return;
      flashNew(p?.eventId);
      loadAll();
    };
    const onAny = (p) => { if (matchesPlant(p)) loadAll(); };
    const offs = [
      eventStream.subscribe('alarm.triggered',    onTrigger),
      eventStream.subscribe('alarm.escalated',    onTrigger),
      eventStream.subscribe('alarm.cleared',      onAny),
      eventStream.subscribe('alarm.acknowledged', onAny),
      eventStream.subscribe('alarm.shelved',      onAny),
      eventStream.subscribe('alarm.unshelved',    onAny)
    ];

    // Fallback poll every 60s — if SSE somehow drops without reconnecting,
    // we still recover to a fresh state without the user noticing.
    const t = setInterval(loadAll, 60_000);

    return () => {
      offs.forEach(off => off());
      clearInterval(t);
    };
  }, [plantId]);

  async function handleAck(eventId) {
    try { await alarmService.ack(eventId); loadAll(); }
    catch (err) { setError(err.message); }
  }
  async function handleShelve(configId) {
    const hours = parseFloat(prompt('Shelve for how many hours?', '4'));
    if (!hours || hours <= 0) return;
    try { await alarmService.shelve(configId, hours); loadAll(); }
    catch (err) { setError(err.message); }
  }
  async function handleUnshelve(configId) {
    try { await alarmService.unshelve(configId); loadAll(); }
    catch (err) { setError(err.message); }
  }
  async function handleDelete(cfg) {
    if (!confirm(`Delete alarm "${cfg.name}"?`)) return;
    try { await alarmService.deleteConfig(cfg._id); loadAll(); }
    catch (err) { setError(err.message); }
  }

  if (!plantId) {
    return <div style={{ padding: 24, color: C.muted, background: C.pageBg }}>Select a plant to manage alarms.</div>;
  }

  const unackCount = events.filter(e => !e.acknowledgedAt).length;

  return (
    <div style={{ padding: '14px 20px 20px', display: 'flex', flexDirection: 'column', gap: 12, background: C.pageBg, color: C.text, minHeight: '100%' }}>
      {/* Header — single row: title · plant picker · updated · refresh · new alarm */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0, fontSize: 16, fontWeight: 600, color: C.text }}>
          <Bell size={16} /> Alarms
        </h2>
        {onChangePlant ? (
          plants.length > 0 ? (
            <select
              style={{ ...ipt, padding: '5px 10px', fontSize: 12, minWidth: 140 }}
              value={plantId} onChange={(e) => onChangePlant(e.target.value)}
            >
              {plants.map(p => <option key={p.plantId} value={p.plantId}>{p.name || p.plantId}</option>)}
            </select>
          ) : (
            <span style={{ fontSize: 11, color: C.muted, fontStyle: 'italic' }}>
              No plants — ask an admin for access.
            </span>
          )
        ) : (
          <span style={{ color: C.muted, fontSize: 13 }}>— {plantId}</span>
        )}
        <span style={{ color: C.muted, fontSize: 11, marginLeft: 'auto' }}>
          {loading ? 'Loading…' : `Updated ${new Date().toLocaleTimeString()}`}
        </span>
        <button style={ghostBtn} onClick={loadAll} title="Refresh"><RefreshCw size={13} /></button>
        <button
          style={{ ...btn, background: C.accent, color: '#fff', borderColor: C.accent, padding: '6px 12px', fontWeight: 500 }}
          onClick={() => setEditing({ ...EMPTY_CONFIG, plantId })}
        >
          <Plus size={13} /> New alarm
        </button>
      </div>

      {error && (
        <div style={{ padding: 10, background: 'rgba(239,68,68,0.1)', color: '#fca5a5', borderRadius: 6, border: '1px solid rgba(239,68,68,0.3)' }}>
          {error}
        </div>
      )}

      {/* Tab bar */}
      <TabBar
        tab={tab} onChange={setTab}
        tabs={[
          { id: 'overview', label: 'Overview', icon: Activity },
          { id: 'active',   label: 'Active',   icon: AlertTriangle, badge: unackCount || null, badgeKind: unackCount ? 'bad' : null },
          { id: 'rules',    label: 'Rules',    icon: Bell,          badge: configs.length || null },
          { id: 'groups',   label: 'Groups',   icon: Users,         badge: groups.length || null },
          { id: 'history',  label: 'History',  icon: History }
        ]}
      />

      {tab === 'overview' && (
        <OverviewTab
          stats={stats} events={events} configs={configs}
          onJumpToActive={() => setTab('active')}
          onJumpToRules={() => setTab('rules')}
        />
      )}

      {tab === 'active' && (
        <ActiveTab
          events={events} recentPulse={recentPulse}
          onAck={handleAck} onShelve={handleShelve}
        />
      )}

      {tab === 'rules' && (
        <RulesTab
          configs={configs} onEdit={setEditing}
          onShelve={handleShelve} onUnshelve={handleUnshelve} onDelete={handleDelete}
        />
      )}

      {tab === 'groups' && (
        <NotifGroupsCard plantId={plantId} groups={groups} onChange={loadAll} />
      )}

      {tab === 'history' && (
        <HistoryTab history={history} onReload={loadHistory} />
      )}

      {editing && (
        <AlarmEditorModal
          initial={editing}
          groups={groups}
          plantId={plantId}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); loadAll(); }}
        />
      )}
    </div>
  );
}

function AlarmEditorModal({ initial, groups, plantId, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({ ...EMPTY_CONFIG, ...initial, plantId }));
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);
  const isEdit = !!initial._id;

  // ── Dynamic dropdown sources ──
  // These get populated by fetching the user's actual MySQL/QuestDB schema.
  // Lists empty until the relevant prerequisite is selected (e.g. tables only
  // load after a database is picked).
  const [databases, setDatabases] = useState([]);     // [string] — MySQL only
  const [tables,    setTables]    = useState([]);     // [string]
  const [fields,    setFields]    = useState([]);     // [string] — columns of the selected table
  const [loadingDb, setLoadingDb] = useState(false);
  const [loadingTbl,setLoadingTbl]= useState(false);
  const [loadingFld,setLoadingFld]= useState(false);

  function set(field, v) { setForm(f => ({ ...f, [field]: v })); }

  // 1) Fetch MySQL databases when engine = mysql
  useEffect(() => {
    if (form.dbEngine !== 'mysql') { setDatabases([]); return; }
    setLoadingDb(true);
    mysqlService.getDatabases()
      .then(rows => {
        const list = (rows || []).map(r => Object.values(r)[0])
          .filter(db => !['information_schema', 'performance_schema', 'mysql', 'sys'].includes(db));
        setDatabases(list);
      })
      .catch(err => console.warn('mysql databases:', err.message))
      .finally(() => setLoadingDb(false));
  }, [form.dbEngine]);

  // 2) Fetch tables when (engine + database) is set. For QuestDB the agent
  //    provides tables() via the questdb service.
  useEffect(() => {
    let cancelled = false;
    async function loadTables() {
      setLoadingTbl(true);
      try {
        let list = [];
        if (form.dbEngine === 'mysql') {
          if (!form.mysqlDatabase) { setTables([]); return; }
          const rows = await mysqlService.getTables(form.mysqlDatabase);
          list = (rows || []).map(r => Object.values(r)[0]);
        } else if (form.dbEngine === 'questdb') {
          if (!plantId) { setTables([]); return; }
          const rows = await questdbService.query('SELECT table_name FROM tables()', plantId);
          list = (rows || []).map(r => r.table_name || Object.values(r)[0]);
        }
        if (!cancelled) setTables(list);
      } catch (err) {
        if (!cancelled) console.warn('tables fetch:', err.message);
      } finally {
        if (!cancelled) setLoadingTbl(false);
      }
    }
    loadTables();
    return () => { cancelled = true; };
  }, [form.dbEngine, form.mysqlDatabase, plantId]);

  // 3) Fetch column names when a table is picked. Uses a LIMIT 1 query to
  //    introspect; falls back to DESCRIBE for empty MySQL tables.
  useEffect(() => {
    let cancelled = false;
    async function loadFields() {
      if (!form.table) { setFields([]); return; }
      setLoadingFld(true);
      try {
        let result = [];
        if (form.dbEngine === 'mysql') {
          if (!form.mysqlDatabase) { setFields([]); return; }
          result = await mysqlService.query(`SELECT * FROM \`${form.table}\` LIMIT 1`, form.mysqlDatabase);
        } else {
          result = await questdbService.query(`SELECT * FROM ${form.table} LIMIT 1`, plantId);
        }
        let cols = result.length ? Object.keys(result[0]) : [];
        // Empty table — try DESCRIBE for MySQL
        if (cols.length === 0 && form.dbEngine === 'mysql') {
          const desc = await mysqlService.query(`DESCRIBE \`${form.table}\``, form.mysqlDatabase);
          cols = desc.map(r => r.Field);
        }
        if (!cancelled) setFields(cols);
        // Auto-guess timestamp field if user hasn't picked one yet
        if (!cancelled && !form.timestampField) {
          const ts = cols.find(f => /time|date|timestamp|created|updated|_ts/i.test(f));
          if (ts) set('timestampField', ts);
        }
      } catch (err) {
        if (!cancelled) console.warn('fields fetch:', err.message);
      } finally {
        if (!cancelled) setLoadingFld(false);
      }
    }
    loadFields();
    return () => { cancelled = true; };
  }, [form.dbEngine, form.mysqlDatabase, form.table, plantId]);

  async function save() {
    setSaving(true); setError(null);
    try {
      const body = {
        ...form,
        limitValue:   parseFloat(form.limitValue) || 0,
        deadband:     parseFloat(form.deadband) || 0,
        delaySeconds: parseInt(form.delaySeconds, 10) || 0,
        notifGroupId: form.notifGroupId || null,
        escalations:  (form.escalations || [])
          .filter(e => e.notifGroupId && Number(e.delayMin) > 0)
          .map(e => ({
            delayMin: parseInt(e.delayMin, 10),
            notifGroupId: e.notifGroupId,
            alsoNotifyOriginal: !!e.alsoNotifyOriginal
          }))
      };
      if (isEdit) await alarmService.updateConfig(initial._id, body);
      else        await alarmService.createConfig(body);
      onSaved();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div className="miralys-modal" style={modalBox} onClick={(e) => e.stopPropagation()}>
        <div style={modalHeader}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600 }}>{isEdit ? 'Edit alarm' : 'New alarm'}</div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
              {isEdit ? `Plant ${plantId} · ${initial.name || ''}` : `New rule for plant ${plantId}`}
            </div>
          </div>
          <button
            className="miralys-ghost"
            style={{ ...ghostBtn, padding: '6px 8px' }}
            onClick={onClose}
            title="Close"
          ><X size={14} /></button>
        </div>

        <div style={modalBody}>
          {error && (
            <div style={{
              padding: '10px 12px', marginBottom: 16,
              background: 'rgba(239,68,68,0.1)', color: '#fca5a5',
              borderRadius: 6, fontSize: 12, border: '1px solid rgba(239,68,68,0.25)'
            }}>{error}</div>
          )}

          {/* Identity */}
          <Section title="Identity" subtitle="What this alarm is called.">
            <Row label={<>Name <span style={{ color: C.bad }}>*</span></>}>
              <input style={ipt} value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Boiler-2 overpressure" />
            </Row>
            <Row label="Description">
              <input style={ipt} value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="Optional — explain what this alarm protects" />
            </Row>
          </Section>

          {/* Data source */}
          <Section title="Data source" subtitle="Where the value comes from.">

            <Row label="Engine">
              <select style={ipt} value={form.dbEngine} onChange={(e) => set('dbEngine', e.target.value)}>
                <option value="mysql">MySQL</option>
                <option value="questdb">QuestDB</option>
              </select>
            </Row>
            {form.dbEngine === 'mysql' && (
              <Row label="MySQL database">
                <select style={ipt}
                        value={form.mysqlDatabase}
                        onChange={(e) => { set('mysqlDatabase', e.target.value); set('table', ''); set('tagField', ''); set('timestampField', ''); }}>
                  <option value="">{loadingDb ? 'Loading…' : '— pick a database —'}</option>
                  {databases.map(db => <option key={db} value={db}>{db}</option>)}
                  {/* If editing an existing rule pointing at a DB not in the list, preserve the value */}
                  {form.mysqlDatabase && !databases.includes(form.mysqlDatabase) && (
                    <option value={form.mysqlDatabase}>{form.mysqlDatabase} (existing)</option>
                  )}
                </select>
              </Row>
            )}
            <Row label="Table">
              <select style={ipt}
                      value={form.table}
                      disabled={form.dbEngine === 'mysql' && !form.mysqlDatabase}
                      onChange={(e) => { set('table', e.target.value); set('tagField', ''); set('timestampField', ''); }}>
                <option value="">
                  {form.dbEngine === 'mysql' && !form.mysqlDatabase ? 'pick a database first' :
                   loadingTbl ? 'Loading…' : '— pick a table —'}
                </option>
                {tables.map(t => <option key={t} value={t}>{t}</option>)}
                {form.table && !tables.includes(form.table) && (
                  <option value={form.table}>{form.table} (existing)</option>
                )}
              </select>
            </Row>
            <Row label="Tag field">
              <select style={ipt}
                      value={form.tagField}
                      disabled={!form.table}
                      onChange={(e) => set('tagField', e.target.value)}>
                <option value="">
                  {!form.table ? 'pick a table first' :
                   loadingFld ? 'Loading…' : '— pick the column to monitor —'}
                </option>
                {fields.map(f => <option key={f} value={f}>{f}</option>)}
                {form.tagField && !fields.includes(form.tagField) && (
                  <option value={form.tagField}>{form.tagField} (existing)</option>
                )}
              </select>
            </Row>
            <Row
              label="Timestamp field(s)"
              hint="For tables with one timestamp column, pick that one. For tables that split it (e.g. DATNR + HR + MIN + SEC), pick them in priority order — the alarm engine will ORDER BY all of them DESC to find the latest row."
            >
              <TimestampFieldPicker
                value={form.timestampField}
                onChange={(v) => set('timestampField', v)}
                fields={fields}
                disabled={!form.table}
                loading={loadingFld}
              />
            </Row>
          </Section>

          {/* Breach */}
          <Section title="Breach condition" subtitle="When the value crosses the limit (with optional deadband + on-delay to suppress noise).">

            <Row label="Alarm type">
              <select style={ipt} value={form.alarmType} onChange={(e) => set('alarmType', e.target.value)}>
                <option value="HI-HI">HI-HI</option>
                <option value="HI">HI</option>
                <option value="LO">LO</option>
                <option value="LO-LO">LO-LO</option>
                <option value="DIGITAL">DIGITAL (value != 0)</option>
                <option value="DEVIATION">DEVIATION from set point</option>
              </select>
            </Row>
            <Row label={<>Limit <span style={{ color: C.bad }}>*</span></>} hint="Threshold value to compare against.">
              <input style={ipt} type="number" value={form.limitValue} onChange={(e) => set('limitValue', e.target.value)} placeholder="e.g. 100" />
            </Row>
            <Row label="Unit">
              <input style={ipt} value={form.unit} onChange={(e) => set('unit', e.target.value)} placeholder="e.g. °C, bar, %" />
            </Row>
            <Row label="Deadband" hint="Hysteresis band. Value must drop below (limit − deadband) before alarm clears. Prevents flapping near the limit.">
              <input style={ipt} type="number" value={form.deadband} onChange={(e) => set('deadband', e.target.value)} placeholder="0" />
            </Row>
            <Row label="On-delay (sec)" hint="Value must stay breached for this many seconds before the alarm fires. 0 = fire immediately.">
              <input style={ipt} type="number" value={form.delaySeconds} onChange={(e) => set('delaySeconds', e.target.value)} placeholder="0" />
            </Row>
          </Section>

          {/* Filters — the new AND/OR/IN/NOT IN builder */}
          <Section
            title="Filter (WHERE clause)"
            subtitle="Restrict which rows trigger this alarm. Supports =, !=, <, >, <=, >=, IN, NOT IN, BETWEEN, LIKE, IS NULL — nested with AND / OR."
          >
            <FilterBuilder value={form.filters} onChange={(v) => set('filters', v)} fields={fields} />
          </Section>

          {/* Routing */}
          <Section title="Routing" subtitle="Who gets notified immediately when this alarm fires.">

            <Row label="Priority">
              <select style={ipt} value={form.priority} onChange={(e) => set('priority', e.target.value)}>
                <option value="critical">critical</option>
                <option value="high">high</option>
                <option value="medium">medium</option>
                <option value="low">low</option>
              </select>
            </Row>
            <Row label="Notify group">
              <select style={ipt} value={form.notifGroupId || ''} onChange={(e) => set('notifGroupId', e.target.value)}>
                <option value="">— none —</option>
                {groups.map(g => <option key={g._id} value={g._id}>{g.name}</option>)}
              </select>
            </Row>
            <Row label="Enabled">
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                <input type="checkbox" checked={form.enabled} onChange={(e) => set('enabled', e.target.checked)} />
                Active
              </label>
            </Row>
          </Section>

          {/* Escalation policy */}
          <Section
            title="Escalation Policy"
            subtitle="If still UNACK after each delay, notify the chosen group too. Delays are minutes AFTER the previous step. Ack or clear halts the chain."
          >
            {(form.escalations || []).map((step, idx) => (
              <div key={idx} style={{
                display: 'grid',
                gridTemplateColumns: 'auto 80px 1fr auto auto',
                gap: 6, alignItems: 'center', marginBottom: 6,
                padding: 6, background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 4
              }}>
                <span style={{ fontSize: 11, color: C.muted, padding: '0 4px' }}>L{idx + 1}: after</span>
                <input
                  type="number" min="1"
                  style={{ ...ipt, padding: '4px 6px' }}
                  value={step.delayMin}
                  onChange={(e) => {
                    const next = [...form.escalations];
                    next[idx] = { ...step, delayMin: e.target.value };
                    set('escalations', next);
                  }}
                />
                <select
                  style={{ ...ipt, padding: '4px 6px' }}
                  value={step.notifGroupId || ''}
                  onChange={(e) => {
                    const next = [...form.escalations];
                    next[idx] = { ...step, notifGroupId: e.target.value };
                    set('escalations', next);
                  }}
                >
                  <option value="">— pick group —</option>
                  {groups.map(g => <option key={g._id} value={g._id}>{g.name}</option>)}
                </select>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: C.muted, whiteSpace: 'nowrap' }}>
                  <input
                    type="checkbox"
                    checked={!!step.alsoNotifyOriginal}
                    onChange={(e) => {
                      const next = [...form.escalations];
                      next[idx] = { ...step, alsoNotifyOriginal: e.target.checked };
                      set('escalations', next);
                    }}
                  />
                  + original
                </label>
                <button
                  style={{
                    background: 'transparent', border: 'none', color: '#ef4444',
                    cursor: 'pointer', fontSize: 16, padding: '0 6px'
                  }}
                  onClick={() => set('escalations', form.escalations.filter((_, i) => i !== idx))}
                  title="Remove this level"
                >×</button>
              </div>
            ))}
            <button
              style={{
                ...btn, fontSize: 11, padding: '4px 10px',
                borderStyle: 'dashed', color: C.muted
              }}
              onClick={() => set('escalations', [
                ...(form.escalations || []),
                { delayMin: 10, notifGroupId: '', alsoNotifyOriginal: false }
              ])}
            >+ Add escalation level</button>
          </Section>
        </div>

        <div style={modalFooter}>
          {(() => {
            const missing = [];
            if (!form.name?.trim()) missing.push('name');
            if (!form.table) missing.push('table');
            if (!form.tagField) missing.push('tag');
            if (form.limitValue === '' || form.limitValue == null || isNaN(parseFloat(form.limitValue))) missing.push('limit');
            const canSave = missing.length === 0;
            return (
              <>
                {!canSave && (
                  <span style={{ fontSize: 11, color: C.muted, marginRight: 'auto' }}>
                    Missing: {missing.join(', ')}
                  </span>
                )}
                <button style={btn} onClick={onClose} disabled={saving}>Cancel</button>
                <button
                  style={{
                    ...btn,
                    background: canSave ? C.accent : C.border,
                    color: '#fff',
                    borderColor: canSave ? C.accent : C.border,
                    cursor: canSave ? 'pointer' : 'not-allowed',
                    opacity: canSave ? 1 : 0.6
                  }}
                  onClick={save}
                  disabled={saving || !canSave}
                  title={canSave ? '' : `Fill in: ${missing.join(', ')}`}
                >
                  {saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Create alarm')}
                </button>
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

// ─── Tab bar ───────────────────────────────────────────────────────────────
function TabBar({ tab, onChange, tabs }) {
  return (
    <div style={{
      display: 'flex', gap: 4,
      borderBottom: `1px solid ${C.border}`,
      overflowX: 'auto', padding: '0 2px'
    }}>
      {tabs.map(t => {
        const Icon = t.icon;
        const active = tab === t.id;
        return (
          <button
            key={t.id}
            onClick={() => onChange(t.id)}
            onFocus={(e) => e.currentTarget.style.outline = 'none'}
            style={{
              position: 'relative',
              padding: '10px 14px 11px', fontSize: 13, fontWeight: active ? 600 : 500,
              background: 'transparent', border: 'none', outline: 'none',
              color: active ? C.text : C.muted, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 7,
              whiteSpace: 'nowrap',
              transition: 'color 0.15s'
            }}
            onMouseEnter={(e) => { if (!active) e.currentTarget.style.color = '#cbd5e1'; }}
            onMouseLeave={(e) => { if (!active) e.currentTarget.style.color = C.muted; }}
          >
            {Icon && <Icon size={14} strokeWidth={active ? 2.25 : 1.75} />}
            {t.label}
            {t.badge != null && (
              <span style={{
                minWidth: 16, height: 16, padding: '0 5px',
                borderRadius: 8, fontSize: 10, fontWeight: 600,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                background: t.badgeKind === 'bad' ? 'rgba(239,68,68,0.15)' : 'rgba(148,163,184,0.12)',
                color:      t.badgeKind === 'bad' ? '#f87171'              : '#94a3b8'
              }}>{t.badge}</span>
            )}
            {/* Active underline — sits BELOW the parent's border-bottom so it overlaps cleanly */}
            {active && (
              <span style={{
                position: 'absolute', left: 8, right: 8, bottom: -1, height: 2,
                background: C.accent, borderRadius: 2
              }} />
            )}
          </button>
        );
      })}
    </div>
  );
}

// ─── Live countdown for "next escalation in Xm Ys" ────────────────────────
function useCountdown(targetIso) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!targetIso) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [targetIso]);
  if (!targetIso) return null;
  const ms = new Date(targetIso).getTime() - now;
  if (ms <= 0) return { label: 'now', urgent: true };
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  const label = m > 0 ? `${m}m ${s}s` : `${s}s`;
  return { label, urgent: m === 0 };
}

// ─── Overview tab ─────────────────────────────────────────────────────────
function OverviewTab({ stats, events, configs, onJumpToActive, onJumpToRules }) {
  const recent = events.slice(0, 5);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
          <KPI label="Active"   value={stats.active}  emphasis={stats.active > 0 ? 'bad' : 'good'} />
          <KPI label="Unack"    value={stats.unack}   emphasis={stats.unack > 0 ? 'bad' : 'good'} />
          <KPI label="Shelved"  value={stats.shelved} emphasis="warn" />
          <KPI label="Normal"   value={stats.normal}  emphasis="good" />
          <KPI label="Today"    value={stats.today} />
        </div>
      )}
      <Card title="Most recent events">
        {recent.length === 0
          ? <div style={empty}>No active alarms. All clear.</div>
          : <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {recent.map(ev => <CompactEventRow key={ev._id} ev={ev} />)}
              <button style={{ ...btn, alignSelf: 'flex-start', marginTop: 4 }} onClick={onJumpToActive}>
                View all active <ArrowUpRight size={12} />
              </button>
            </div>}
      </Card>
      <Card title={`${configs.length} alarm rule${configs.length === 1 ? '' : 's'} configured`}>
        <button style={btn} onClick={onJumpToRules}>Manage rules <ArrowUpRight size={12} /></button>
      </Card>
    </div>
  );
}

function CompactEventRow({ ev }) {
  const ps = PRIORITY_STYLE[ev.priority] || {};
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '4px 90px 1fr 80px 70px',
      gap: 10, padding: '8px 12px',
      borderRadius: 6,
      fontSize: 12, alignItems: 'center',
      transition: 'background 0.15s'
    }}
      onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.025)'}
      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
    >
      <span style={{ width: 3, height: 22, borderRadius: 2, background: ps.color }} />
      <span style={{ fontSize: 10, fontWeight: 600, color: ps.color, letterSpacing: '0.04em' }}>
        {ev.priority?.toUpperCase()}
      </span>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        <span style={{ fontWeight: 500 }}>{ev.config?.name || ev.tagField}</span>
        <span style={{ color: C.muted, marginLeft: 8, fontSize: 11 }}>{ev.alarmType}</span>
      </span>
      <span style={{ color: ps.color, fontWeight: 600 }}>{ev.valueAtEvent}</span>
      <span style={{ color: C.muted, fontSize: 11, textAlign: 'right' }}>
        {timeAgo(ev.triggeredAt)}
      </span>
    </div>
  );
}

// ─── Active tab — cards for critical/high, table for medium/low ─────────────
function ActiveTab({ events, recentPulse, onAck, onShelve }) {
  const [filter, setFilter] = useState('all');   // all | unack | critical | high
  const [view, setView]     = useState('auto');  // auto | cards | table

  const filtered = useMemo(() => {
    return events.filter(ev => {
      if (filter === 'unack')    return !ev.acknowledgedAt;
      if (filter === 'critical') return ev.priority === 'critical';
      if (filter === 'high')     return ev.priority === 'high';
      return true;
    });
  }, [events, filter]);

  const high   = filtered.filter(e => e.priority === 'critical' || e.priority === 'high');
  const others = filtered.filter(e => e.priority !== 'critical' && e.priority !== 'high');

  if (events.length === 0) {
    return (
      <Card title="No active alarms">
        <div style={{ ...empty, padding: 40 }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>✓</div>
          All clear. Nothing to see here.
        </div>
      </Card>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <FilterPill active={filter === 'all'}      onClick={() => setFilter('all')}>All ({events.length})</FilterPill>
        <FilterPill active={filter === 'unack'}    onClick={() => setFilter('unack')}>Unack only ({events.filter(e => !e.acknowledgedAt).length})</FilterPill>
        <FilterPill active={filter === 'critical'} onClick={() => setFilter('critical')}>Critical ({events.filter(e => e.priority === 'critical').length})</FilterPill>
        <FilterPill active={filter === 'high'}     onClick={() => setFilter('high')}>High ({events.filter(e => e.priority === 'high').length})</FilterPill>
        <span style={{ flex: 1 }} />
        <select style={ipt} value={view} onChange={(e) => setView(e.target.value)} title="View mode">
          <option value="auto">View: Auto</option>
          <option value="cards">All as cards</option>
          <option value="table">All as table</option>
        </select>
      </div>

      {/* Cards for critical/high (or all if cards mode) */}
      {(view === 'cards' || view === 'auto') && (view === 'cards' ? filtered : high).length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: 12 }}>
          {(view === 'cards' ? filtered : high).map(ev => (
            <ActiveCard key={ev._id} ev={ev} pulse={recentPulse.has(ev._id)} onAck={onAck} onShelve={onShelve} />
          ))}
        </div>
      )}

      {/* Compact table for medium/low (auto mode), or all (table mode) */}
      {((view === 'auto' && others.length > 0) || (view === 'table' && filtered.length > 0)) && (
        <Card title={view === 'table' ? `All (${filtered.length})` : `Medium / Low (${others.length})`}>
          <ActiveTable events={view === 'table' ? filtered : others} onAck={onAck} />
        </Card>
      )}
    </div>
  );
}

function FilterPill({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      cursor: 'pointer', outline: 'none',
      background: active ? C.accentSoft : 'transparent',
      border: `1px solid ${active ? 'rgba(167,139,250,0.4)' : C.cardBorder}`,
      color: active ? C.accent : C.muted,
      fontSize: 11, padding: '5px 12px',
      borderRadius: 999,
      fontWeight: active ? 600 : 500,
      transition: 'all 0.15s'
    }}>{children}</button>
  );
}

function ActiveCard({ ev, pulse, onAck, onShelve }) {
  const ps = PRIORITY_STYLE[ev.priority] || {};
  const isCritical = ev.priority === 'critical';
  const countdown = useCountdown(ev.nextEscalationAt);
  const nextStep = (ev.escalationChain || [])[ev.escalationLevel || 0];

  return (
    <div style={{
      position: 'relative',
      borderRadius: 8,
      background: isCritical ? 'linear-gradient(135deg, rgba(239,68,68,0.10), rgba(255,255,255,0.025))' : C.cardBg,
      border: `1px solid ${isCritical ? 'rgba(239,68,68,0.35)' : C.cardBorder}`,
      // colored left accent stripe instead of a thick all-around border
      boxShadow: `inset 3px 0 0 0 ${ps.color}`,
      padding: '10px 12px 10px 14px',
      display: 'flex', flexDirection: 'column', gap: 6,
      animation: pulse ? 'alarmFadeIn 0.6s ease-out' : isCritical ? 'criticalPulse 2s ease-in-out infinite' : 'none'
    }}>
      <style>{`
        @keyframes alarmFadeIn { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: none; } }
        @keyframes criticalPulse { 0%,100% { box-shadow: inset 3px 0 0 0 ${ps.color}, 0 0 0 0 rgba(239,68,68,0); } 50% { box-shadow: inset 3px 0 0 0 ${ps.color}, 0 0 0 4px rgba(239,68,68,0.12); } }
      `}</style>

      {/* Row 1: title + priority */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {ev.config?.name || ev.tagField}
        </div>
        <span style={{ fontSize: 10, fontWeight: 600, color: ps.color, letterSpacing: '0.04em' }}>
          {ev.priority?.toUpperCase()}
        </span>
      </div>

      {/* Row 2: value (big) + limit + meta on right */}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
          <span style={{ fontSize: 24, fontWeight: 700, color: ps.color, lineHeight: 1 }}>
            {ev.valueAtEvent}
          </span>
          <span style={{ fontSize: 11, color: C.muted }}>
            / {ev.limitValue} {ev.config?.unit || ''}
          </span>
        </div>
        <div style={{ fontSize: 10, color: C.muted, textAlign: 'right' }}>
          {ev.alarmType} · {ev.tagField} · {timeAgo(ev.triggeredAt)}
        </div>
      </div>

      {/* Row 3: countdown if escalating */}
      {countdown && nextStep && (
        <div style={{
          padding: '4px 8px', borderRadius: 4, fontSize: 10,
          background: countdown.urgent ? 'rgba(239,68,68,0.12)' : 'rgba(245,158,11,0.08)',
          color: countdown.urgent ? '#fca5a5' : '#fdba74',
          display: 'flex', justifyContent: 'space-between'
        }}>
          <span>↗ L{(ev.escalationLevel || 0) + 1} in <b>{countdown.label}</b></span>
          <span>→ {nextStep.notifGroupName}</span>
        </div>
      )}

      {/* Row 4: actions */}
      <div style={{ display: 'flex', gap: 6 }}>
        {!ev.acknowledgedAt && (
          <button onClick={() => onAck(ev._id)} style={{
            flex: 1, padding: '6px 10px', borderRadius: 5, border: 'none', cursor: 'pointer', outline: 'none',
            background: C.good, color: '#fff', fontSize: 12, fontWeight: 600,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5
          }}>
            <Check size={12} /> Acknowledge
          </button>
        )}
        {ev.config?._id && (
          <button onClick={() => onShelve(ev.config._id)} title="Shelve" style={{
            cursor: 'pointer', outline: 'none', padding: '6px 10px', borderRadius: 5,
            background: 'transparent', border: `1px solid ${C.cardBorder}`, color: C.muted, fontSize: 11,
            display: 'inline-flex', alignItems: 'center', gap: 4
          }}>
            <PauseCircle size={12} /> Shelve
          </button>
        )}
      </div>
    </div>
  );
}

function ActiveTable({ events, onAck }) {
  return (
    <table style={tbl}>
      <thead><tr>
        <th style={th}>Triggered</th>
        <th style={th}>Name</th>
        <th style={th}>Type</th>
        <th style={th}>Value</th>
        <th style={th}>Limit</th>
        <th style={th}>Priority</th>
        <th style={th}>State</th>
        <th style={th}>Notified</th>
        <th style={th}>Actions</th>
      </tr></thead>
      <tbody>
        {events.map(ev => {
          const ps = PRIORITY_STYLE[ev.priority] || {};
          return (
            <tr key={ev._id} style={{ transition: 'background 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}
                onMouseLeave={e => e.currentTarget.style.background = ''}>
              <td style={{ ...td, color: C.muted, fontSize: 11 }}>{timeAgo(ev.triggeredAt)}</td>
              <td style={td}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 3, height: 18, borderRadius: 2, background: ps.color }} />
                  {ev.config?.name || ev.tagField}
                </div>
              </td>
              <td style={{ ...td, color: C.muted }}>{ev.alarmType}</td>
              <td style={{ ...td, color: ps.color, fontWeight: 600 }}>{ev.valueAtEvent}</td>
              <td style={{ ...td, color: C.muted }}>{ev.limitValue}</td>
              <td style={{ ...td, color: ps.color, fontWeight: 500, fontSize: 11, letterSpacing: '0.04em' }}>
                {ev.priority?.toUpperCase()}
              </td>
              <td style={{ ...td, color: ev.acknowledgedAt ? C.muted : C.bad, fontSize: 11, fontWeight: 500 }}>
                {ev.acknowledgedAt ? 'ACK' : 'UNACK'}
              </td>
              <td style={{ ...td, color: C.muted, fontSize: 11 }}>{(ev.notifiedVia || []).join(', ') || '—'}</td>
              <td style={td}>
                {!ev.acknowledgedAt && (
                  <button style={ghostBtn} onClick={() => onAck(ev._id)} title="Acknowledge">
                    <Check size={12} />
                  </button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ─── Rules tab — search + filter + sortable columns ───────────────────────
function RulesTab({ configs, onEdit, onShelve, onUnshelve, onDelete }) {
  const [search, setSearch]       = useState('');
  const [priorityF, setPriorityF] = useState('all');
  const [stateF, setStateF]       = useState('all');
  const [sortKey, setSortKey]     = useState('name');
  const [sortDir, setSortDir]     = useState('asc');

  const rows = useMemo(() => {
    let r = configs.slice();
    if (search.trim()) {
      const q = search.toLowerCase();
      r = r.filter(c =>
        c.name?.toLowerCase().includes(q) ||
        c.description?.toLowerCase().includes(q) ||
        c.tagField?.toLowerCase().includes(q) ||
        c.table?.toLowerCase().includes(q)
      );
    }
    if (priorityF !== 'all') r = r.filter(c => c.priority === priorityF);
    if (stateF !== 'all')    r = r.filter(c => (c.state?.state || 'NORMAL') === stateF);
    r.sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      const va = sortValue(a, sortKey), vb = sortValue(b, sortKey);
      if (va < vb) return -1 * dir;
      if (va > vb) return  1 * dir;
      return 0;
    });
    return r;
  }, [configs, search, priorityF, stateF, sortKey, sortDir]);

  function sortValue(c, key) {
    switch (key) {
      case 'name':      return (c.name || '').toLowerCase();
      case 'priority':  return ({ critical: 0, high: 1, medium: 2, low: 3 }[c.priority] ?? 99);
      case 'state':     return c.state?.state || 'NORMAL';
      case 'lastValue': return c.state?.lastValue ?? -Infinity;
      default:          return '';
    }
  }

  function toggleSort(key) {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  }

  function SortableTh({ children, k }) {
    const active = sortKey === k;
    return (
      <th style={{ ...th, cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort(k)}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: active ? C.text : C.muted }}>
          {children}
          {active && (sortDir === 'asc' ? <ChevronUp size={11} /> : <ChevronDown size={11} />)}
        </span>
      </th>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 240px', minWidth: 200 }}>
          <Search size={13} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: C.muted, pointerEvents: 'none' }} />
          <input
            style={{ ...ipt, width: '100%', paddingLeft: 28 }}
            placeholder="Search rules by name, tag, table…"
            value={search} onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select style={ipt} value={priorityF} onChange={(e) => setPriorityF(e.target.value)}>
          <option value="all">All priorities</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
        <select style={ipt} value={stateF} onChange={(e) => setStateF(e.target.value)}>
          <option value="all">All states</option>
          <option value="NORMAL">Normal</option>
          <option value="UNACK">Unack</option>
          <option value="ACTIVE">Active</option>
          <option value="SHELVED">Shelved</option>
          <option value="PENDING">Pending</option>
        </select>
        <span style={{ fontSize: 11, color: C.muted, marginLeft: 'auto' }}>
          {rows.length} / {configs.length}
        </span>
      </div>

      <Card title={null}>
        {rows.length === 0
          ? <div style={empty}>{configs.length === 0 ? 'No alarm rules yet. Click "New alarm" to add one.' : 'No matches for current filters.'}</div>
          : <table style={tbl}>
              <thead><tr>
                <SortableTh k="name">Name</SortableTh>
                <th style={th}>Source</th>
                <th style={th}>Tag</th>
                <th style={th}>Type</th>
                <th style={th}>Limit</th>
                <SortableTh k="priority">Priority</SortableTh>
                <SortableTh k="state">State</SortableTh>
                <SortableTh k="lastValue">Last value</SortableTh>
                <th style={th}>Actions</th>
              </tr></thead>
              <tbody>
                {rows.map(cfg => {
                  const st = cfg.state?.state || 'NORMAL';
                  const ps = PRIORITY_STYLE[cfg.priority] || {};
                  const stateColor = st === 'NORMAL' ? C.good : st === 'SHELVED' ? C.warn : C.bad;
                  return (
                    <tr key={cfg._id} style={{ opacity: cfg.enabled ? 1 : 0.5, transition: 'background 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}
                        onMouseLeave={e => e.currentTarget.style.background = ''}>
                      <td style={td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ width: 3, height: 22, borderRadius: 2, background: ps.color }} />
                          <div>
                            <div style={{ fontWeight: 500 }}>{cfg.name}</div>
                            {cfg.description && <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{cfg.description}</div>}
                          </div>
                        </div>
                      </td>
                      <td style={{ ...td, color: C.muted, fontSize: 11 }}>
                        {cfg.dbEngine}{cfg.mysqlDatabase ? `·${cfg.mysqlDatabase}` : ''} / {cfg.table}
                      </td>
                      <td style={{ ...td, fontSize: 11 }}>{cfg.tagField}</td>
                      <td style={{ ...td, color: C.muted }}>{cfg.alarmType}</td>
                      <td style={td}>{cfg.limitValue} <span style={{ color: C.muted }}>{cfg.unit}</span></td>
                      <td style={{ ...td, color: ps.color, fontWeight: 500, fontSize: 11, letterSpacing: '0.04em' }}>
                        {cfg.priority?.toUpperCase()}
                      </td>
                      <td style={td}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, color: stateColor }}>
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: stateColor }} />
                          {st}
                        </span>
                      </td>
                      <td style={td}>{cfg.state?.lastValue != null ? cfg.state.lastValue : <span style={{ color: C.muted }}>—</span>}</td>
                      <td style={td}>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button style={ghostBtn} onClick={() => onEdit(cfg)} title="Edit"><Edit2 size={12} /></button>
                          {st === 'SHELVED'
                            ? <button style={ghostBtn} onClick={() => onUnshelve(cfg._id)} title="Un-shelve"><PlayCircle size={12} /></button>
                            : <button style={ghostBtn} onClick={() => onShelve(cfg._id)}   title="Shelve"><PauseCircle size={12} /></button>}
                          <button style={{ ...ghostBtn, color: C.bad }} onClick={() => onDelete(cfg)} title="Delete"><Trash2 size={12} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>}
      </Card>
    </div>
  );
}

// ─── History tab — timeline grouped per alarm ────────────────────────────
const EVENT_TYPE_STYLE = {
  TRIGGERED:    { color: '#fca5a5', bg: 'rgba(239,68,68,0.15)',  icon: '!' },
  ESCALATED:    { color: '#fdba74', bg: 'rgba(245,158,11,0.18)', icon: '↗' },
  ACKNOWLEDGED: { color: '#a78bfa', bg: 'rgba(167,139,250,0.18)', icon: '✓' },
  CLEARED:      { color: '#86efac', bg: 'rgba(34,197,94,0.15)',  icon: '○' },
  SHELVED:      { color: '#fdba74', bg: 'rgba(245,158,11,0.15)', icon: '🔇' }
};

function HistoryTab({ history, onReload }) {
  const [search, setSearch]   = useState('');
  const [typeF, setTypeF]     = useState('all');

  const filtered = useMemo(() => {
    return history.filter(h => {
      if (typeF !== 'all' && h.eventType !== typeF) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        if (!(h.tagField || '').toLowerCase().includes(q) &&
            !(h.notes || '').toLowerCase().includes(q) &&
            !(h.ackBy || '').toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [history, search, typeF]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 240px', minWidth: 200 }}>
          <Search size={13} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: C.muted, pointerEvents: 'none' }} />
          <input
            style={{ ...ipt, width: '100%', paddingLeft: 28 }}
            placeholder="Search by tag, notes, or who acked…"
            value={search} onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select style={ipt} value={typeF} onChange={(e) => setTypeF(e.target.value)}>
          <option value="all">All events</option>
          <option value="TRIGGERED">Triggered</option>
          <option value="ESCALATED">Escalated</option>
          <option value="ACKNOWLEDGED">Acknowledged</option>
          <option value="CLEARED">Cleared</option>
          <option value="SHELVED">Shelved</option>
        </select>
        <button style={btn} onClick={onReload}><RefreshCw size={12} /> Reload</button>
        <span style={{ fontSize: 11, color: C.muted, marginLeft: 'auto' }}>
          {filtered.length} / {history.length}
        </span>
      </div>

      <Card title={null}>
        {filtered.length === 0
          ? <div style={empty}>{history.length === 0 ? 'No history yet.' : 'No matches for current filters.'}</div>
          : <div style={{ display: 'flex', flexDirection: 'column' }}>
              {filtered.map((h, idx) => {
                const s = EVENT_TYPE_STYLE[h.eventType] || EVENT_TYPE_STYLE.TRIGGERED;
                return (
                  <div key={h._id || idx} style={{
                    display: 'grid', gridTemplateColumns: '90px 24px 1fr auto',
                    gap: 12, padding: '10px 4px',
                    borderBottom: idx < filtered.length - 1 ? `1px solid ${C.border}` : 'none',
                    alignItems: 'center'
                  }}>
                    <span style={{ fontSize: 11, color: C.muted }}>{timeAgo(h.triggeredAt)}</span>
                    <span style={{
                      width: 24, height: 24, borderRadius: '50%',
                      background: s.bg, color: s.color,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 12, fontWeight: 700
                    }}>{s.icon}</span>
                    <div>
                      <div style={{ fontSize: 12 }}>
                        <span style={{ color: s.color, fontWeight: 600 }}>{h.eventType}</span>
                        {' · '}
                        <span style={{ color: C.text }}>{h.tagField || 'alarm'}</span>
                        {h.valueAtEvent != null && (
                          <span style={{ color: C.muted }}>{' '}({h.valueAtEvent}{h.limitValue != null ? ` vs ${h.limitValue}` : ''})</span>
                        )}
                        {h.ackBy && <span style={{ color: C.muted }}> by {h.ackBy}</span>}
                      </div>
                      {h.notes && <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{h.notes}</div>}
                    </div>
                    <span style={{ fontSize: 11, color: C.muted }}>{new Date(h.triggeredAt).toLocaleString()}</span>
                  </div>
                );
              })}
            </div>}
      </Card>
    </div>
  );
}

// ─── small helper: time ago ───────────────────────────────────────────────
function timeAgo(iso) {
  if (!iso) return '—';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60)    return `${s}s ago`;
  if (s < 3600)  return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function KPI({ label, value, emphasis }) {
  const colors = { good: C.good, bad: C.bad, warn: C.warn };
  const color = colors[emphasis] || C.text;
  return (
    <div style={{
      padding: '14px 16px',
      borderRadius: 10,
      background: C.cardBg,
      border: `1px solid ${C.cardBorder}`
    }}>
      <div style={{ color: C.muted, fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color, marginTop: 6, lineHeight: 1 }}>{value}</div>
    </div>
  );
}
function Card({ title, children }) {
  return (
    <div style={{
      padding: title ? '14px 16px 16px' : 4,
      borderRadius: 10,
      background: C.cardBg,
      border: `1px solid ${C.cardBorder}`
    }}>
      {title && (
        <div style={{
          fontWeight: 600, fontSize: 13, color: C.text,
          marginBottom: 12, letterSpacing: '0.01em'
        }}>{title}</div>
      )}
      {children}
    </div>
  );
}
// ─── Timestamp field picker — supports composite (DATNR + HR + MIN + SEC) ──
// Stored as a comma-separated string ("DATNR,HR,MIN,SEC") so it round-trips
// through the existing single-string AlarmConfig.timestampField field.
function TimestampFieldPicker({ value, onChange, fields, disabled, loading }) {
  const selected = String(value || '').split(',').map(s => s.trim()).filter(Boolean);
  const available = (fields || []).filter(f => !selected.includes(f));

  function add(field) {
    const next = [...selected, field];
    onChange(next.join(','));
  }
  function remove(field) {
    onChange(selected.filter(f => f !== field).join(','));
  }
  function move(field, dir) {
    const idx = selected.indexOf(field);
    const target = idx + dir;
    if (target < 0 || target >= selected.length) return;
    const next = selected.slice();
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next.join(','));
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* Selected chips, ordered */}
      {selected.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          {selected.map((f, idx) => (
            <span key={f} style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '4px 4px 4px 10px', borderRadius: 6,
              background: C.accentSoft, color: C.accent,
              border: `1px solid rgba(167,139,250,0.3)`, fontSize: 12
            }}>
              <span style={{ fontSize: 10, color: C.muted, marginRight: 2 }}>{idx + 1}.</span>
              {f}
              {selected.length > 1 && (
                <>
                  <button
                    type="button" title="Move up"
                    onClick={() => move(f, -1)}
                    disabled={idx === 0}
                    style={{ background: 'transparent', border: 'none', color: C.muted, cursor: idx === 0 ? 'not-allowed' : 'pointer', padding: 2, opacity: idx === 0 ? 0.3 : 1 }}
                  ><ChevronUp size={11} /></button>
                  <button
                    type="button" title="Move down"
                    onClick={() => move(f, 1)}
                    disabled={idx === selected.length - 1}
                    style={{ background: 'transparent', border: 'none', color: C.muted, cursor: idx === selected.length - 1 ? 'not-allowed' : 'pointer', padding: 2, opacity: idx === selected.length - 1 ? 0.3 : 1 }}
                  ><ChevronDown size={11} /></button>
                </>
              )}
              <button
                type="button" title="Remove"
                onClick={() => remove(f)}
                style={{ background: 'transparent', border: 'none', color: C.muted, cursor: 'pointer', padding: '2px 6px' }}
              ><X size={11} /></button>
            </span>
          ))}
        </div>
      )}

      {/* Add field — only shows fields not already selected */}
      <select
        value=""
        disabled={disabled || loading || available.length === 0}
        onChange={(e) => { if (e.target.value) add(e.target.value); }}
        style={ipt}
      >
        <option value="">
          {disabled ? 'pick a table first'
            : loading ? 'Loading…'
            : selected.length === 0 ? '— pick the timestamp column —'
            : available.length === 0 ? 'All columns added'
            : '+ Add another column'}
        </option>
        {available.map(f => <option key={f} value={f}>{f}</option>)}
      </select>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{
        fontSize: 11, color: '#cbd5e1', fontWeight: 600,
        textTransform: 'uppercase', letterSpacing: '0.08em',
        marginBottom: subtitle ? 4 : 12,
        paddingBottom: 6,
        borderBottom: `1px solid ${C.cardBorder}`
      }}>
        {title}
      </div>
      {subtitle && (
        <div style={{ fontSize: 11, color: C.muted, marginBottom: 12, lineHeight: 1.5 }}>{subtitle}</div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{children}</div>
    </div>
  );
}
function Row({ label, children, hint }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 14, alignItems: 'start' }}>
      <label style={{ color: C.muted, fontSize: 12, paddingTop: 8, fontWeight: 500 }}>{label}</label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {children}
        {hint && <div style={{ fontSize: 11, color: C.muted }}>{hint}</div>}
      </div>
    </div>
  );
}

// ─── Notification groups editor ────────────────────────────────────────────
// Each group bundles "where to send" (channels) + "who to send to" (members per
// channel) + a priority gate + a cooldown. Alarm rules pick one of these.
// Members are stored per channel because each channel takes a different shape:
//   email     → array of email addresses
//   telegram  → array of chat IDs
//   webhook   → array of generic POST URLs
//   gchat     → array of Google Chat space webhook URLs

const EMPTY_GROUP = {
  name: '', description: '',
  channels: { email: false, telegram: false, webhook: false, gchat: false },
  members: { email: [], telegram: [], webhook: [], gchat: [] },
  minPriority: 'medium', cooldownMin: 10,
  canAcknowledge: false, canSnooze: false, canShelve: false,
  messageTemplates: { telegram: '', email: '', gchat: '', webhook: '' }
};

const CHANNEL_LABELS = {
  email:    { label: 'Email',         placeholder: 'ops@plant.com, manager@plant.com' },
  telegram: { label: 'Telegram',      placeholder: 'chat IDs, e.g. -100123456789' },
  webhook:  { label: 'Generic webhook', placeholder: 'https://hooks.example.com/alarms' },
  gchat:    { label: 'Google Chat',   placeholder: 'https://chat.googleapis.com/v1/spaces/.../messages?key=...&token=...' }
};

function NotifGroupsCard({ plantId, groups, onChange }) {
  const [editing, setEditing] = useState(null);

  async function handleDelete(g) {
    if (!confirm(`Delete notification group "${g.name}"?`)) return;
    try { await alarmService.deleteGroup(g._id); onChange(); }
    catch (err) { alert(err.message); }
  }

  return (
    <Card title={`Notification groups (${groups.length})`}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <button style={{ ...btn, background: C.accent, color: '#fff', borderColor: C.accent }}
                onClick={() => setEditing({ ...EMPTY_GROUP, plantId })}>
          <Plus size={12} /> New group
        </button>
      </div>
      {groups.length === 0
        ? <div style={empty}>No notification groups yet. Create one before adding alarms.</div>
        : <table style={tbl}>
            <thead><tr>
              <th style={th}>Name</th>
              <th style={th}>Channels</th>
              <th style={th}>Members</th>
              <th style={th}>Min priority</th>
              <th style={th}>Cooldown</th>
              <th style={th}>Actions</th>
            </tr></thead>
            <tbody>
              {groups.map(g => {
                const channels = typeof g.channels === 'string' ? JSON.parse(g.channels) : (g.channels || {});
                const members  = typeof g.members  === 'string' ? JSON.parse(g.members)  : (g.members  || {});
                const activeChs = Object.entries(channels).filter(([, on]) => on).map(([k]) => k);
                const totalMembers = activeChs.reduce((s, ch) => s + ((members[ch] || []).length), 0);
                return (
                  <tr key={g._id}>
                    <td style={td}><b>{g.name}</b>{g.description && <div style={{ fontSize: 11, color: C.muted }}>{g.description}</div>}</td>
                    <td style={td}>{activeChs.join(', ') || '—'}</td>
                    <td style={td}>{totalMembers}</td>
                    <td style={td}>{g.minPriority}</td>
                    <td style={td}>{g.cooldownMin}m</td>
                    <td style={td}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button style={btn} onClick={() => setEditing(g)} title="Edit"><Edit2 size={12} /></button>
                        <button style={{ ...btn, color: C.bad }} onClick={() => handleDelete(g)} title="Delete"><Trash2 size={12} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>}
      {editing && (
        <NotifGroupEditorModal
          initial={editing}
          plantId={plantId}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onChange(); }}
        />
      )}
    </Card>
  );
}

function NotifGroupEditorModal({ initial, plantId, onClose, onSaved }) {
  // Normalise: turn any stored string-JSON back into objects, and ensure each
  // channel has a (possibly empty) member array.
  const normalised = useMemo(() => {
    const channels = typeof initial.channels === 'string' ? JSON.parse(initial.channels) : (initial.channels || {});
    const members  = typeof initial.members  === 'string' ? JSON.parse(initial.members)  : (initial.members  || {});
    const out = { ...EMPTY_GROUP, ...initial, plantId,
      channels: { ...EMPTY_GROUP.channels, ...channels },
      members:  { ...EMPTY_GROUP.members,  ...members }
    };
    for (const ch of Object.keys(EMPTY_GROUP.members)) {
      if (!Array.isArray(out.members[ch])) out.members[ch] = [];
    }
    return out;
  }, [initial, plantId]);

  const [form, setForm] = useState(normalised);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);
  const isEdit = !!initial._id;

  // Keep the raw textarea text per channel so the user can freely type commas
  // and trailing whitespace without us shredding it on each keystroke. We only
  // split/normalise into the members[] array right before saving.
  const [membersText, setMembersText] = useState(() => {
    const out = {};
    for (const ch of Object.keys(EMPTY_GROUP.members)) {
      out[ch] = (normalised.members[ch] || []).join(', ');
    }
    return out;
  });

  function setChannel(ch, on) { setForm(f => ({ ...f, channels: { ...f.channels, [ch]: on } })); }

  function updateMembersText(ch, text) {
    setMembersText(prev => ({ ...prev, [ch]: text }));
  }

  function countRecipients(ch) {
    return String(membersText[ch] || '').split(/[\n,]/).map(s => s.trim()).filter(Boolean).length;
  }

  async function save() {
    setSaving(true); setError(null);
    try {
      // Normalise text → array right at save time so commas-while-typing are safe.
      const members = {};
      for (const ch of Object.keys(EMPTY_GROUP.members)) {
        members[ch] = String(membersText[ch] || '').split(/[\n,]/).map(s => s.trim()).filter(Boolean);
      }
      const body = {
        ...form,
        members,
        cooldownMin: parseInt(form.cooldownMin, 10) || 10
      };
      if (isEdit) await alarmService.updateGroup(initial._id, body);
      else        await alarmService.createGroup(body);
      onSaved();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  const [showTemplates, setShowTemplates] = useState(
    !!(form.messageTemplates && Object.values(form.messageTemplates).some(v => v))
  );

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div className="miralys-modal" style={modalBox} onClick={(e) => e.stopPropagation()}>
        <div style={modalHeader}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600 }}>{isEdit ? 'Edit notification group' : 'New notification group'}</div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
              {isEdit ? `Plant ${plantId} · ${initial.name || ''}` : `Where alarms in plant ${plantId} get sent`}
            </div>
          </div>
          <button className="miralys-ghost" style={{ ...ghostBtn, padding: '6px 8px' }} onClick={onClose} title="Close"><X size={14} /></button>
        </div>

        <div style={modalBody}>
          {error && (
            <div style={{
              padding: '10px 12px', marginBottom: 16,
              background: 'rgba(239,68,68,0.1)', color: '#fca5a5',
              borderRadius: 6, fontSize: 12, border: '1px solid rgba(239,68,68,0.25)'
            }}>{error}</div>
          )}

          {/* Identity */}
          <Section title="Identity" subtitle="What to call this group.">
            <Row label="Name">
              <input style={ipt} value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Operations, On-call SREs" />
            </Row>
            <Row label="Description">
              <input style={ipt} value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Optional — who or what this group covers" />
            </Row>
          </Section>

          {/* Policy */}
          <Section title="Policy" subtitle="Filter rules and protect against alert storms.">
            <Row label="Min priority" hint="Lower-priority alarms won't notify this group.">
              <select style={ipt} value={form.minPriority} onChange={(e) => setForm(f => ({ ...f, minPriority: e.target.value }))}>
                <option value="critical">Critical only</option>
                <option value="high">High and above</option>
                <option value="medium">Medium and above</option>
                <option value="low">Everything (low and above)</option>
              </select>
            </Row>
            <Row label="Cooldown" hint="Suppress repeat notifications for the same (alarm, channel) within this many minutes.">
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="number" style={{ ...ipt, width: 100 }} value={form.cooldownMin}
                  onChange={(e) => setForm(f => ({ ...f, cooldownMin: e.target.value }))} />
                <span style={{ fontSize: 12, color: C.muted }}>minutes</span>
              </div>
            </Row>
          </Section>

          {/* Channels */}
          <Section
            title="Channels & recipients"
            subtitle="Enable a channel, then add recipients (comma- or newline-separated). For Google Chat, paste the full incoming-webhook URL — it already contains its own auth."
          >
            {Object.entries(CHANNEL_LABELS).map(([ch, meta]) => {
              const enabled = !!form.channels[ch];
              const text  = membersText[ch] || '';
              const count = countRecipients(ch);
              return (
                <div key={ch} style={{
                  borderRadius: 8,
                  border: `1px solid ${enabled ? 'rgba(167,139,250,0.25)' : C.cardBorder}`,
                  background: enabled ? 'rgba(167,139,250,0.04)' : 'transparent',
                  transition: 'all 0.15s'
                }}>
                  <div
                    onClick={() => setChannel(ch, !enabled)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 12px', cursor: 'pointer', userSelect: 'none',
                      fontSize: 13, color: C.text, fontWeight: 500
                    }}
                  >
                    <Toggle checked={enabled} />
                    <span style={{ flex: 1 }}>{meta.label}</span>
                    {enabled && count > 0 && (
                      <span style={{ fontSize: 11, color: C.muted, fontWeight: 400 }}>
                        {count} recipient{count > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  {enabled && (
                    <div style={{ padding: '0 12px 10px' }}>
                      <textarea
                        style={{ ...ipt, fontFamily: 'ui-monospace, monospace', fontSize: 12, minHeight: 56, resize: 'vertical', width: '100%' }}
                        placeholder={meta.placeholder}
                        value={text}
                        onChange={(e) => updateMembersText(ch, e.target.value)}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </Section>

          {/* Action permissions */}
          <Section
            title="Action buttons in notifications"
            subtitle="Anyone who receives the message can click these buttons (signed link, no login). Only enable for trusted groups."
          >
            <PermissionToggle
              checked={!!form.canAcknowledge}
              onChange={(on) => setForm(f => ({ ...f, canAcknowledge: on }))}
              icon="✓" label="Acknowledge"
              hint="One click stops escalation."
            />
            <PermissionToggle
              checked={!!form.canSnooze}
              onChange={(on) => setForm(f => ({ ...f, canSnooze: on }))}
              icon="💤" label="Snooze 30m"
              hint="Pauses escalation for 30 min. Re-fires if still active."
            />
            <PermissionToggle
              checked={!!form.canShelve}
              onChange={(on) => setForm(f => ({ ...f, canShelve: on }))}
              icon="🔇" label="Shelve (4h + 24h)"
              hint="Silences the rule entirely until the window expires."
            />
          </Section>

          {/* Custom templates — collapsible */}
          <Section
            title={
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
                    onClick={() => setShowTemplates(s => !s)}>
                {showTemplates ? <ChevronDown size={12} /> : <ChevronUp size={12} style={{ transform: 'rotate(90deg)' }} />}
                Custom message templates (optional)
              </span>
            }
            subtitle={showTemplates ? 'Leave blank to use the built-in default for that channel.' : 'Click to customize per-channel message bodies.'}
          >
            {showTemplates && (
              <>
                <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Placeholders:</div>
                <div style={{
                  padding: '8px 10px', borderRadius: 6,
                  background: C.input, border: `1px solid ${C.cardBorder}`,
                  fontSize: 10.5, color: C.text, fontFamily: 'ui-monospace, monospace',
                  lineHeight: 1.7, wordBreak: 'break-all'
                }}>
                  {`{{name}} {{value}} {{limit}} {{unit}} {{priority}} {{plant}} {{table}} {{tagField}} {{alarmType}} {{timestamp}} {{description}} {{ackUrl}} {{snoozeUrl}} {{shelve4hUrl}} {{shelve24hUrl}}`}
                </div>
                {['telegram', 'email', 'gchat', 'webhook'].map(ch => (
                  <Row key={ch} label={ch.charAt(0).toUpperCase() + ch.slice(1)}>
                    <textarea
                      style={{ ...ipt, fontFamily: 'ui-monospace, monospace', fontSize: 12, minHeight: 48, resize: 'vertical', width: '100%' }}
                      placeholder={`(empty → built-in ${ch} template)`}
                      value={form.messageTemplates?.[ch] || ''}
                      onChange={(e) => setForm(f => ({
                        ...f,
                        messageTemplates: { ...(f.messageTemplates || {}), [ch]: e.target.value }
                      }))}
                    />
                  </Row>
                ))}
              </>
            )}
          </Section>
        </div>

        <div style={modalFooter}>
          <button style={btn} onClick={onClose} disabled={saving}>Cancel</button>
          <button
            style={{ ...btn, background: C.accent, color: '#fff', borderColor: C.accent, padding: '8px 16px', fontWeight: 500 }}
            onClick={save}
            disabled={saving || !form.name?.trim()}
            title={!form.name?.trim() ? 'Group name is required' : ''}
          >
            {saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Create group')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── iOS-style toggle (visual only — wrapping element owns the click) ───
function Toggle({ checked }) {
  return (
    <span style={{
      width: 32, height: 18, borderRadius: 999,
      background: checked ? C.accent : 'rgba(148,163,184,0.25)',
      position: 'relative', flexShrink: 0,
      transition: 'background 0.15s'
    }}>
      <span style={{
        position: 'absolute', top: 2, left: checked ? 16 : 2,
        width: 14, height: 14, borderRadius: '50%',
        background: '#fff',
        transition: 'left 0.15s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
      }} />
    </span>
  );
}

// ─── Permission row with toggle + icon + label + hint ────────────────────
function PermissionToggle({ checked, onChange, icon, label, hint }) {
  return (
    <div
      onClick={() => onChange(!checked)}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
        background: checked ? 'rgba(167,139,250,0.04)' : 'transparent',
        border: `1px solid ${checked ? 'rgba(167,139,250,0.25)' : C.cardBorder}`,
        transition: 'all 0.15s', userSelect: 'none'
      }}
    >
      <Toggle checked={checked} />
      <span style={{ fontSize: 16, lineHeight: 1 }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, color: C.text, fontWeight: 500 }}>{label}</div>
        {hint && <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{hint}</div>}
      </div>
    </div>
  );
}

const empty = { padding: '32px 20px', textAlign: 'center', color: C.muted, fontSize: 13 };
const tbl   = { width: '100%', borderCollapse: 'collapse', fontSize: 12, color: C.text };
const th    = { textAlign: 'left', padding: '10px 12px', borderBottom: `1px solid ${C.cardBorder}`, color: C.muted, fontWeight: 500, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' };
const td    = { padding: '10px 12px', borderBottom: `1px solid ${C.cardBorder}` };
const modalOverlay = {
  position: 'fixed', inset: 0,
  background: 'rgba(0,0,0,0.72)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  padding: 20
};
const modalBox = {
  width: 'min(760px, 100%)', maxHeight: '92vh',
  display: 'flex', flexDirection: 'column',
  background: '#111827',                       // solid — no glass wash
  border: `1px solid ${C.cardBorder}`,
  borderRadius: 12,
  color: C.text,
  boxShadow: '0 20px 60px -10px rgba(0,0,0,0.8), 0 0 0 1px rgba(0,0,0,0.4)'
};
const modalHeader = {
  padding: '14px 20px',
  borderBottom: `1px solid ${C.cardBorder}`,
  display: 'flex', justifyContent: 'space-between', alignItems: 'center'
};
const modalBody = { padding: '18px 24px', overflowY: 'auto', flex: 1 };
const modalFooter = {
  padding: '12px 20px',
  borderTop: `1px solid ${C.cardBorder}`,
  display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8
};
